import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminHeaderPhotos, adminCreateHeaderPhoto, adminApproveHeaderPhoto, adminDeleteHeaderPhoto } from "../lib/api";
import { Toolbar, Modal, Field, Card, inputCls } from "./AdminLayout";
import { IconBtn, Badge } from "./ArticlesAdmin";
import { Trash2, Check as CheckIcon, Clock } from "lucide-react";
import ImageUpload from "../components/ImageUpload";

const empty = { image_url: "", photographer: "", location: "", caption: "", credit_link: "" };

export default function HeaderPhotosAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const load = () => adminHeaderPhotos().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_url || !form.photographer.trim()) return toast.error("Photo and photographer are required.");
    setBusy(true);
    try { await adminCreateHeaderPhoto({ ...form, consent: true }); toast.success("Photo added to rotation."); setOpen(false); setForm(empty); load(); }
    catch { toast.error("Could not save photo."); }
    finally { setBusy(false); }
  };
  const approve = async (p) => { await adminApproveHeaderPhoto(p.id); toast.success("Photo approved."); load(); };
  const remove = async (p) => { if (!window.confirm("Delete this photo?")) return; await adminDeleteHeaderPhoto(p.id); toast.success("Photo deleted."); load(); };
  const pending = items.filter((p) => p.status === "pending").length;

  return (
    <div>
      <Toolbar title="Header Photos" subtitle="Reader-submitted photos for the Nassau Life header. Approved photos rotate daily." onAdd={() => setOpen(true)} addLabel="Add Photo" />
      {pending > 0 && <div data-testid="header-photos-pending-banner" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800">{pending} photo{pending > 1 ? "s" : ""} awaiting your approval</div>}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((p) => (
          <Card key={p.id} data-testid={`admin-header-photo-${p.id}`} className="overflow-hidden">
            <img src={p.image_url} alt={p.caption} className="h-40 w-full object-cover" />
            <div className="flex items-start justify-between gap-2 p-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[var(--navy)]">{p.photographer}</p>
                <p className="truncate text-xs text-slate-400">{[p.location, p.email].filter(Boolean).join(" · ")}</p>
                {p.caption && <p className="truncate text-xs text-slate-500">{p.caption}</p>}
                {p.credit_link && <p className="truncate text-xs text-sky-700">{p.credit_link}</p>}
                <div className="mt-1">{p.status === "pending" ? <Badge icon={Clock} cls="bg-amber-100 text-amber-700">Pending</Badge> : <Badge icon={CheckIcon} cls="bg-emerald-100 text-emerald-700">In rotation</Badge>}</div>
              </div>
              <div className="flex gap-2">
                {p.status === "pending" && <IconBtn onClick={() => approve(p)} testid={`approve-header-photo-${p.id}`}><CheckIcon size={15} className="text-emerald-600" /></IconBtn>}
                <IconBtn onClick={() => remove(p)} danger testid={`delete-header-photo-${p.id}`}><Trash2 size={15} /></IconBtn>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {open && (
        <Modal title="Add Header Photo" onClose={() => setOpen(false)} onSubmit={submit} busy={busy} submitLabel="Add to Rotation">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Photo" full><ImageUpload value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} testid="ahp-image" /></Field>
            <Field label="Photographer credit"><input data-testid="ahp-photographer" value={form.photographer} onChange={set("photographer")} className={inputCls} /></Field>
            <Field label="Location"><input data-testid="ahp-location" value={form.location} onChange={set("location")} className={inputCls} /></Field>
            <Field label="Caption"><input data-testid="ahp-caption" value={form.caption} onChange={set("caption")} className={inputCls} /></Field>
            <Field label="Credit link (optional)"><input data-testid="ahp-link" value={form.credit_link} onChange={set("credit_link")} className={inputCls} /></Field>
          </div>
        </Modal>
      )}
    </div>
  );
}
